import { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { createPortal } from 'react-dom';
import { useEditorDeviceProfile } from '../../DragAndDrop/useEditorDragCapability.js';
import { getToolbarButtonClassName } from '../../utilities/editorUiUtils.js';
import { EDITOR_MODES } from '../../utilities/simpleEditorMode.js';
import { SAVE_STATUSES } from '../../utilities/saveStatus.js';
import useEscapeToClose from '../../utilities/useEscapeToClose.js';

const SAVE_STATUS_DETAILS = {
  [SAVE_STATUSES.SAVED]: {
    label: 'Saved',
    actionLabel: 'Portfolio saved',
    iconClass: 'bi-save2',
  },
  [SAVE_STATUSES.UNSAVED]: {
    label: 'Unsaved changes',
    actionLabel: 'Save portfolio. Unsaved changes',
    iconClass: 'bi-floppy2',
  },
  [SAVE_STATUSES.SAVING]: {
    label: 'Saving...',
    actionLabel: 'Saving portfolio',
    iconClass: '',
  },
  [SAVE_STATUSES.ERROR]: {
    label: 'Could not save',
    actionLabel: 'Try saving portfolio again. Previous save failed',
    iconClass: 'bi-exclamation-circle-fill',
  },
};

/**
 * Fixed toolbar for document-level actions. Element actions live beside the
 * selected canvas node so this toolbar stays compact on small screens.
 */
const EditButtons = ({
  saveToJSON,
  setShowClassStyleMenu,
  showClassStyleMenu,
  setShowFontMenu,
  showFontMenu = false,
  aiEnabled = false,
  onOpenAI,
  onUndo,
  onRedo,
  canUndo = false,
  canRedo = false,
  aiQuotaExceeded = false,
  aiQueueCount = 0,
  aiQueueWorking = false,
  aiQueueNeedsAttention = false,
  selectedItemLabel = '',
  editorMode = EDITOR_MODES.ADVANCED,
  saveStatus = SAVE_STATUSES.UNSAVED,
}) => {
  const simpleMode = editorMode === EDITOR_MODES.SIMPLE;
  const { isCompact } = useEditorDeviceProfile();
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  const moreMenuRef = useRef(null);
  const morePopoverRef = useRef(null);
  const saveStatusDetails = SAVE_STATUS_DETAILS[saveStatus] || SAVE_STATUS_DETAILS[SAVE_STATUSES.UNSAVED];
  const saveStatusIndicator = (
    <button
      className={`save-status-indicator is-${saveStatus}`}
      type="button"
      onClick={saveToJSON}
      disabled={saveStatus === SAVE_STATUSES.SAVED || saveStatus === SAVE_STATUSES.SAVING}
      aria-live="polite"
      aria-atomic="true"
      aria-label={saveStatusDetails.actionLabel}
      title={saveStatusDetails.label}
    >
      {saveStatus === SAVE_STATUSES.SAVING ? (
        <span className="spinner-border spinner-border-sm" aria-hidden="true"></span>
      ) : (
        <i className={`bi ${saveStatusDetails.iconClass}`} aria-hidden="true"></i>
      )}
    </button>
  );
  const historySaveControls = (
    <div className="editor-history-save-controls" role="toolbar" aria-label="History and save actions">
      <button
        className={getToolbarButtonClassName('editor-history-button')}
        type="button"
        onClick={onUndo}
        disabled={!canUndo}
        title="Undo"
        aria-label="Undo"
      >
        <i className="bi bi-arrow-counterclockwise" aria-hidden="true"></i>
      </button>
      <button
        className={getToolbarButtonClassName('editor-history-button')}
        type="button"
        onClick={onRedo}
        disabled={!canRedo}
        title="Redo"
        aria-label="Redo"
      >
        <i className="bi bi-arrow-clockwise" aria-hidden="true"></i>
      </button>
      {saveStatusIndicator}
    </div>
  );
  const secondaryButtons = [
    {
      key: 'fonts',
      iconClass: 'bi-fonts',
      title: 'Portfolio fonts',
      onClick: () => setShowFontMenu(!showFontMenu),
    },
    ...(!simpleMode ? [{
      key: 'classes',
      iconClass: 'bi-brush',
      title: 'Class editor',
      onClick: () => setShowClassStyleMenu(!showClassStyleMenu),
    }] : []),
  ];
  const aiButton = aiEnabled ? {
      key: 'ai',
      iconClass: 'bi-stars',
      title: aiQuotaExceeded
        ? 'Today\'s free AI allowance has been used'
        : selectedItemLabel
        ? `Open AI Assistant for ${selectedItemLabel}`
        : 'Open AI Assistant',
      onClick: onOpenAI,
      disabled: aiQuotaExceeded,
    } : null;

  useEscapeToClose(showMoreMenu, () => setShowMoreMenu(false));

  useEffect(() => {
    if (!showMoreMenu) return undefined;

    const closeOutside = (event) => {
      const insideButton = moreMenuRef.current?.contains(event.target);
      const insidePopover = morePopoverRef.current?.contains(event.target);
      if (!insideButton && !insidePopover) setShowMoreMenu(false);
    };
    document.addEventListener('pointerdown', closeOutside);
    return () => document.removeEventListener('pointerdown', closeOutside);
  }, [showMoreMenu]);

  useEffect(() => {
    if (!isCompact) setShowMoreMenu(false);
  }, [isCompact]);

  const runSecondaryAction = (action) => {
    setShowMoreMenu(false);
    action.onClick();
  };

  const portfolioActions = (
    <div className="new-blog-button-container">
      <div className="button-row" role="toolbar" aria-label="Portfolio actions">
        <div className="editor-toolbar-group editor-toolbar-left">
          {!isCompact ? secondaryButtons.map(({
            key, iconClass, title, onClick, disabled = false,
          }) => (
            <button
              key={key}
              className={getToolbarButtonClassName()}
              type="button"
              onClick={onClick}
              disabled={disabled}
              title={title}
              aria-label={title}
            >
              <i className={`bi ${iconClass}`} aria-hidden="true"></i>
            </button>
          )) : (
            <div className="editor-toolbar-more" ref={moreMenuRef}>
              <button
                className={getToolbarButtonClassName()}
                type="button"
                onClick={() => setShowMoreMenu((visible) => !visible)}
                title="More portfolio tools"
                aria-label="More portfolio tools"
                aria-haspopup="menu"
                aria-expanded={showMoreMenu}
              >
                <i className="bi bi-three-dots" aria-hidden="true"></i>
              </button>
            </div>
          )}
        </div>
        <div className="editor-toolbar-group editor-toolbar-right">
          {aiButton ? (
            <button
              className={getToolbarButtonClassName(
                `editor-ai-button${selectedItemLabel ? ' has-selected-item' : ''}`
              )}
              type="button"
              onClick={aiButton.onClick}
              disabled={aiButton.disabled}
              title={aiButton.title}
              aria-label={aiButton.title}
            >
              <i className={`bi ${aiButton.iconClass}`} aria-hidden="true"></i>
              {selectedItemLabel ? (
                <span
                  key={selectedItemLabel}
                  className="editor-ai-selection-indicator"
                  aria-hidden="true"
                >
                  <i className="bi bi-pencil-fill"></i>
                </span>
              ) : null}
              {aiQueueCount > 0 ? (
                <span className={`editor-ai-queue-indicator${
                  aiQueueNeedsAttention ? ' needs-attention' : aiQueueWorking ? ' is-working' : ''
                }`} aria-hidden="true">
                  {aiQueueNeedsAttention ? '!' : aiQueueCount}
                </span>
              ) : null}
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );

  return (
    <>
      {typeof document === 'undefined'
        ? historySaveControls
        : createPortal(historySaveControls, document.body)}

      {typeof document === 'undefined'
        ? portfolioActions
        : createPortal(portfolioActions, document.body)}

      {isCompact && showMoreMenu && typeof document !== 'undefined'
        ? createPortal(
          <div
            className="editor-toolbar-more-menu"
            ref={morePopoverRef}
            role="menu"
            aria-label="More portfolio tools"
          >
            {secondaryButtons.map((action) => (
              <button
                key={action.key}
                className="editor-toolbar-more-item"
                type="button"
                role="menuitem"
                onClick={() => runSecondaryAction(action)}
              >
                <i className={`bi ${action.iconClass}`} aria-hidden="true"></i>
                <span>{action.title}</span>
              </button>
            ))}
          </div>,
          document.body
        )
        : null}
    </>
  );
};

EditButtons.propTypes = {
  saveToJSON: PropTypes.func.isRequired,
  setShowClassStyleMenu: PropTypes.func.isRequired,
  showClassStyleMenu: PropTypes.bool.isRequired,
  setShowFontMenu: PropTypes.func.isRequired,
  showFontMenu: PropTypes.bool,
  aiEnabled: PropTypes.bool,
  onOpenAI: PropTypes.func,
  onUndo: PropTypes.func,
  onRedo: PropTypes.func,
  canUndo: PropTypes.bool,
  canRedo: PropTypes.bool,
  aiQuotaExceeded: PropTypes.bool,
  aiQueueCount: PropTypes.number,
  aiQueueWorking: PropTypes.bool,
  aiQueueNeedsAttention: PropTypes.bool,
  selectedItemLabel: PropTypes.string,
  editorMode: PropTypes.oneOf(Object.values(EDITOR_MODES)),
  saveStatus: PropTypes.oneOf(Object.values(SAVE_STATUSES)),
};

export default EditButtons;
